import {useState, useEffect} from "react";
import AgoraRTC from "agora-rtc-sdk-ng";
import {generateVoiceChatToken as _generateVoiceChatToken} from "api-socket/chat";
import sampleAudio from "components/PopUp/VoiceChatSettingPopUp/sampleAudio.mp3";
import {notify} from "components/VoiceChat/Components/Notify";

export default function useAgora(appId, client, type) {
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [localMicTrack, setLocalMicTrack] = useState(null);
  const [localAudioTrack, setLocalAudioTrack] = useState(null);
  const [localMicDeviceId, setLocalMicDeviceId] = useState(null);
  const [localAudioDeviceId, setLocalAudioDeviceId] = useState(null);
  const [micStatus, setMicStatus] = useState(true);
  const [audioStatus, setAudioStatus] = useState(true);
  const [isUserLeft, setIsUserLeft] = useState(false);
  const [volumeData, setVolumeData] = useState([]);

  useEffect(() => {
    if (!client) return;
    setRemoteUsers(client.remoteUsers);

    const handleUserPublished = async (user, mediaType) => {
      await client.subscribe(user, mediaType);
      setLocalAudioTrack(user.audioTrack);
      AgoraRTC.checkAudioTrackIsActive(user.audioTrack)
        .then(result => setAudioStatus(result ? "on" : "off"))
        .catch(() => setAudioStatus("error"));
      setRemoteUsers(remoteUsers => Array.from(client.remoteUsers));
    };
    const handleUserUnpublished = user => {
      setRemoteUsers(remoteUsers => Array.from(client.remoteUsers));
    };
    const handleUserJoined = user => {
      setRemoteUsers(remoteUsers => Array.from(client.remoteUsers));
      if (type === "private") {
        setIsUserLeft(false);
      }
    };
    const handleUserLeft = user => {
      if (type === "private") {
        setIsUserLeft(true);
        notify("default", "ID_VOICE_CHAT_PRIVATE_CHAT_END");
      }
      setRemoteUsers(remoteUsers => Array.from(client.remoteUsers));
    };
    const handleVolumeIndicator = volumes => {
      const speakUser = [];
      setVolumeData(speakUser);
      volumes.forEach(volume => {
        if (volume.level > 50) {
          setVolumeData([...speakUser, volume.uid]);
        }
      });
    };

    client.enableAudioVolumeIndicator();
    client.on("user-published", handleUserPublished);
    client.on("user-unpublished", handleUserUnpublished);
    client.on("user-joined", handleUserJoined);
    client.on("user-left", handleUserLeft);
    client.on("volume-indicator", handleVolumeIndicator);

    return () => {
      client.off("user-published", handleUserPublished);
      client.off("user-unpublished", handleUserUnpublished);
      client.off("user-joined", handleUserJoined);
      client.off("user-left", handleUserLeft);
      client.off("volume-indicator", handleVolumeIndicator);
    };
  }, [client]);

  async function generateVoiceChatToken(id, channel) {
    const token = await _generateVoiceChatToken({id, channel})
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          return data.voiceChatToken;
        }
      });
    return token;
  }

  async function join(id, channel, token) {
    if (!client) return;
    try {
      const microphoneTrack = await createLocalTracks();
      const result = await AgoraRTC.checkAudioTrackIsActive(microphoneTrack);
      setMicStatus(result ? "on" : "off");
      await client.join(appId, channel, token, id);
      await client.publish(microphoneTrack);
      window.client = client;
    } catch (e) {
      setMicStatus("error");
    }
  }

  async function createLocalTracks(audioConfig) {
    const microphoneTrack = await AgoraRTC.createMicrophoneAudioTrack(audioConfig);
    setLocalMicTrack(microphoneTrack);
    return microphoneTrack;
  }

  async function leave() {
    if (localMicTrack) {
      localMicTrack.stop();
      localMicTrack.close();
    }
    setRemoteUsers([]);
    await client?.leave();
    return "DISCONNECTING";
  }

  async function getDevices() {
    const micList = [];
    const speakerList = [];
    let currentMic = null;
    let currentSpeaker = null;

    await AgoraRTC.getPlaybackDevices().then(devices => {
      if (devices.length === 0) return;
      devices.map((speaker, i) => {
        const speakerInfo = {};
        speakerInfo.deviceId = speaker.deviceId;
        speakerInfo.label = speaker.label;
        if (localAudioDeviceId && localAudioDeviceId === speaker.deviceId) {
          currentSpeaker = speakerInfo;
        } else if (i === 0) {
          currentSpeaker = speakerInfo;
        }
        return speakerList.push(speakerInfo);
      });
    });

    await AgoraRTC.getMicrophones().then(microphones => {
      if (microphones.length === 0) return;
      microphones.map(mic => {
        const micInfo = {};
        micInfo.deviceId = mic.deviceId;
        micInfo.label = mic.label;
        if (mic.label.includes(localMicTrack._deviceName)) {
          currentMic = micInfo;
          setLocalMicDeviceId(mic.deviceId);
        }
        return micList.push(micInfo);
      });
    });

    return {micList, speakerList, currentMic, currentSpeaker};
  }

  function getVolume() {
    return [localAudioTrack ? localAudioTrack._volume : 100, localMicTrack._volume];
  }

  function setVolume(type, value) {
    if (type === "mic") {
      return localMicTrack.setVolume(value);
    } else {
      return localAudioTrack && localAudioTrack.setVolume(value);
    }
  }

  async function micTestStart(userId, setMicTestLevel) {
    let stream = await AgoraRTC.createMicrophoneAudioTrack({
      streamId: userId,
      audio: true,
      microphoneId: localMicDeviceId,
    });
    stream.play();
    const intervalId = setInterval(function () {
      setMicTestLevel(stream.getVolumeLevel());
    }, 500);
    return {stream, intervalId};
  }

  async function micTestStop(micTestInfo) {
    micTestInfo.stream.stop();
    clearInterval(micTestInfo.intervalId);
  }

  async function speakerTestStart() {
    const track = await AgoraRTC.createBufferSourceAudioTrack({
      source: sampleAudio,
    });
    track.startProcessAudioBuffer({loop: true});
    track.play();
    return track;
  }

  function speakerTestStop(track) {
    track.stop();
  }

  function switchDevice(type, deviceId) {
    if (type === "mic") {
      localMicTrack.setDevice(deviceId);
      setLocalMicDeviceId(deviceId);
    } else {
      remoteUsers?.map(user => user.audioTrack.setPlaybackDevice(deviceId));
      setLocalAudioDeviceId(deviceId);
    }
  }

  function setMuted(type) {
    if (type === "mic") {
      const isMuted = localMicTrack.muted;
      setMicStatus(!isMuted ? "off" : "on");
      localMicTrack.setMuted(!isMuted);
    } else {
      if (localAudioTrack) {
        const isPlay = localAudioTrack.isPlaying;
        if (isPlay) {
          localAudioTrack.stop();
          setAudioStatus("off");
        } else {
          localAudioTrack.play();
          setAudioStatus("on");
        }
      }
    }
  }

  return {
    remoteUsers,
    micStatus,
    audioStatus,
    isUserLeft,
    volumeData,
    generateVoiceChatToken,
    join,
    leave,
    getDevices,
    switchDevice,
    getVolume,
    setVolume,
    micTestStart,
    micTestStop,
    speakerTestStart,
    speakerTestStop,
    setMuted,
  };
}
